import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";

/** One typed client. The bearer comes from whoever is logged in; a 401 clears the
 *  session so the router sends the user back through the hosted UI. */
export type IdTokenProvider = () => Promise<string | null>;

let provider: IdTokenProvider = async () => null;
let onUnauthorized: () => void = () => {};

export function bindAuth(p: IdTokenProvider, unauthorized: () => void) {
  provider = p;
  onUnauthorized = unauthorized;
}

const auth: Middleware = {
  async onRequest({ request }) {
    const token = await provider();
    if (token) request.headers.set("authorization", `Bearer ${token}`);
    return request;
  },
  async onResponse({ response }) {
    if (response.status === 401) onUnauthorized();
    return response;
  },
};

/** For the rare non-JSON call (a CSV download) that bypasses the typed client. */
export async function authHeaders(): Promise<HeadersInit> {
  const token = await provider();
  return token ? { authorization: `Bearer ${token}` } : {};
}

export const api = createClient<paths>({ baseUrl: "" });
api.use(auth);

/** Unwrap an openapi-fetch result or throw with the API's error message. */
export function unwrap<T>(r: { data?: T; error?: unknown; response: Response }): T {
  if (r.error !== undefined || !r.response.ok) {
    const e = r.error as { message?: string; error?: string } | undefined;
    throw new Error(e?.message ?? e?.error ?? `${r.response.status} ${r.response.statusText}`);
  }
  return r.data as T;
}
